import { jsonApiRequest, nextPageCursor, unwrapList, unwrapOne } from './jsonApi.js';

/**
 * Client for the **new** cloud-core-api (`REACT_APP_NEW_CORE_API_URL`).
 *
 * ⚠️ A DIFFERENT SERVICE from the Smart Cabinet Root API the rest of this app talks to
 * (`REACT_APP_API_URL`, used by `src/api.js`). This module is the only caller of the
 * `cloud-core-api` repo (`REACT_APP_NEW_CORE_API_URL`, `/api/v2/**`). Never point one env var at
 * the other's host, and never add root-API calls here.
 *
 * Used **only for id translation** — every picker in the discount forms keeps fetching from the
 * legacy endpoints, so no display field comes from here.
 *
 * Merchant path key: `/data/merchant/{m}/*` resolves via `MainMerchantMapper.findIdByName` →
 * `WHERE m.name = ?`, and that column holds husky's **urlname** (same as cds). So every path takes
 * `merchant.urlname`; see `fetchMerchants` for how the rows are shaped to keep that apart from the
 * display name.
 *
 * Every `/data/*` endpoint answers 503 until core-api runs with `IF_CORE_API_MAIN_DB_ENABLED`.
 */

const CORE = () => process.env.REACT_APP_NEW_CORE_API_URL;

// cloud-core-api serves the main_db lookups under /api/v2/data/merchant/{merchant}/{resource}
// (moved there from /api/v2/merchant/{merchant}/data/{resource} in core-api commit 24ecc9b).
const dataUrl = (merchantUrlname, resource) =>
    `${CORE()}/api/v2/data/merchant/${encodeURIComponent(merchantUrlname)}/${resource}`;

// JSON:API resource types core-api checks on write (`JsonApi.USER_GROUP_RESOURCE`, `USER_RESOURCE`).
const USER_GROUP_TYPE = 'usergroups';
const USER_TYPE = 'users';

/** Largest `page[size]` core-api accepts on its paged lists (openapi `maximum: 1000`). */
export const DATA_PAGE_SIZE = 1000;

/**
 * Hard stop for the page loop, far above any real merchant. It only exists so a server bug that
 * keeps returning a `next` link cannot hang the page.
 */
const MAX_DATA_PAGES = 100;

/**
 * Every row of a paged core-api list, as raw JSON:API resources (`{type, id, attributes,
 * relationships}`) so callers that need `relationships` still have them.
 *
 * core-api pages `producttypes`, `devices`, `users` and `merchants` with a keyset cursor and a
 * default `page[size]` of 50, so a single request silently drops everything after the first page.
 * The `next` link is server-relative and repeats our own query, so only its cursor is taken.
 * @param {string} url list URL, optionally already carrying a query string
 * @returns {Promise<Array<object>>}
 */
export async function fetchAllPages(url) {
    const rows = [];
    const seen = new Set();
    const separator = url.includes('?') ? '&' : '?';
    let cursor = null;

    for (let pageNo = 0; pageNo < MAX_DATA_PAGES; pageNo++) {
        const envelope = await jsonApiRequest(`${url}${separator}page[size]=${DATA_PAGE_SIZE}`
            + (cursor ? `&page[cursor]=${encodeURIComponent(cursor)}` : ''));
        if (Array.isArray(envelope?.data)) {
            rows.push(...envelope.data);
        }

        cursor = nextPageCursor(envelope?.links?.next);
        if (!cursor) {
            return rows;
        }
        if (seen.has(cursor)) {
            throw new Error('cloud-core-api returned the same page cursor twice; the list is incomplete.');
        }
        seen.add(cursor);
    }
    throw new Error(`cloud-core-api returned more than ${MAX_DATA_PAGES} pages; the list is incomplete.`);
}

const fetchAllRows = async url => unwrapList({ data: await fetchAllPages(url) });

/**
 * Merchants the caller may open, as `{id, name, urlname, backendType, status}`.
 *
 * From `GET /api/v2/data/merchants`, a main_db read. There `attributes.name` is the unique name
 * every path takes — husky's urlname — and `displayName` is the human-readable one. They are
 * renamed here to the `name` / `urlname` pair the rest of the portal (`matchMerchant`,
 * `merchant.urlname`) already uses; `name` falls back to the urlname when no display name is set.
 *
 * Scoped by the token: admins see every merchant, anyone else only those their token gives the
 * merchant admin role — the same rule the `/data/merchant/{m}/*` endpoints apply, so a merchant
 * missing here could not be read anyway. Closed merchants are left out, as the old encoder-app
 * list did; soft-deleted ones never come back.
 * @returns {Promise<Array<object>>}
 */
export async function fetchMerchants() {
    const rows = await fetchAllRows(`${CORE()}/api/v2/data/merchants?filter[status][ne]=CLOSED`);
    return rows.map(row => ({
        id: row.id,
        name: row.displayName || row.name,
        urlname: row.name,
        backendType: row.backendType,
        status: row.status,
    }));
}

/**
 * Product types: `{id, name, sourceExternalId, productCode, productCodeType, reference, ...}`.
 * `id` is `product_type.id` — the UUID a cds PRODUCT_TYPE target needs — and
 * `sourceExternalId` is the legacy product id, so no client-side hashing is required. Paged.
 * @param {string} merchantUrlname
 * @returns {Promise<Array<object>>}
 */
export function fetchProductTypes(merchantUrlname) {
    return fetchAllRows(dataUrl(merchantUrlname, 'producttypes'));
}

/**
 * Facilities: `{id, name, reference, ...}`. `reference` is the legacy Selfly `location._id`, which
 * is a **free-form string** — UUIDs and codes like `Carlsberg.Kerava` both occur, so never apply a
 * UUID-shape filter to it. Not paged.
 * @param {string} merchantUrlname
 * @returns {Promise<Array<object>>}
 */
export async function fetchFacilities(merchantUrlname) {
    return unwrapList(await jsonApiRequest(dataUrl(merchantUrlname, 'facilities')));
}

/**
 * Merchant devices: `{id, name, displayName, reference, status, timezone, ...}`.
 *
 * `id` is `merchant_device.id` (what a cds DEVICE target needs) and **`name`** is the canonical
 * device code. The underlying husky device is only a relationship now, so it cannot be joined on
 * by mistake. Paged.
 * @param {string} merchantUrlname
 * @returns {Promise<Array<object>>}
 */
export function fetchDevices(merchantUrlname) {
    return fetchAllRows(dataUrl(merchantUrlname, 'devices'));
}

/**
 * Merchant users: `{id, userId, cardAlias, reference, userType, email, ...}` where `id` is
 * `merchant_user.id`. Needed for user-restricted discounts in both directions — resolve a
 * legacy userKey by matching it against `userId` / `cardAlias` / `reference`. See
 * `referenceMaps.matchMerchantUser`. Paged.
 * @param {string} merchantUrlname
 * @returns {Promise<Array<object>>}
 */
export function fetchMerchantUsers(merchantUrlname) {
    return fetchAllRows(dataUrl(merchantUrlname, 'users'));
}

/**
 * User groups: `{id, name, description, reference, groupType, users: [{id}]}` where `id` is
 * `user_group_definition.id` — exactly the UUID a cds `USER_GROUP` target carries — and each
 * `users[].id` is a **`merchant_user.id`**.
 *
 * core-api returns the members as the JSON:API `users` relationship, not as an attribute, so they
 * are lifted into `users` here; `unwrapList` alone would drop them and make every group look empty.
 *
 * This is the only way to expand a `USER_GROUP` target: cds exposes no user-group endpoint (its
 * routes are the seven under `/api/v2/discounts/merchant/{m}/**`) and a discount response carries
 * the group id alone. Without this call a user-restricted discount cannot report who it covers.
 * Not paged.
 * @param {string} merchantUrlname
 * @returns {Promise<Array<object>>}
 */
export async function fetchUserGroups(merchantUrlname) {
    const envelope = await jsonApiRequest(dataUrl(merchantUrlname, 'usergroups'));
    const data = Array.isArray(envelope?.data) ? envelope.data : [];
    return data.map(item => ({
        id: item.id,
        ...(item.attributes || {}),
        users: (item.relationships?.users?.data || []).map(user => ({ id: user.id })),
    }));
}

/**
 * Create a LIST user group and return its `{id}`.
 *
 * `groupType` must be `LIST` — core-api rejects anything else on write, and a CRITERIA group on a
 * SELFLY discount wedges reverse sync permanently (its guard finds no LIST membership).
 *
 * `users` are **`merchant_user.id`**s, so callers translate legacy userKeys first
 * (`referenceMaps.buildUserMap`). They are sent as the `users` relationship: core-api ignores an
 * unknown `users` attribute without complaint, which would save the group with no members.
 *
 * The sync keys its own group per source discount — `id = uuidv3("selfly-ug:{sourceId}")`,
 * `reference = sourceId`. The portal cannot choose the id, but setting the same `reference` keeps
 * a portal-created group traceable to its discount. Two live groups sharing a reference is safe:
 * the reverse handler resolves membership by following the discount's `USER_GROUP` *targets*,
 * never by reference, precisely so a shared one cannot union two audiences together.
 * @param {string} merchantUrlname
 * @param {{name: string, users: Array<{id: string}>, reference?: string, description?: string}} group
 * @returns {Promise<object|null>} `{id}`
 */
export async function createUserGroup(merchantUrlname, { users, ...attributes }) {
    const body = await jsonApiRequest(dataUrl(merchantUrlname, 'usergroups'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            data: {
                type: USER_GROUP_TYPE,
                attributes: { groupType: 'LIST', ...attributes },
                relationships: {
                    users: { data: (users || []).map(user => ({ type: USER_TYPE, id: user.id })) },
                },
            },
        }),
    });
    return unwrapOne(body);
}

/**
 * Delete a user group. Used to retire the group a replaced discount was targeting — a new group is
 * created per write, so without this each edit would leave its predecessor behind with stale
 * membership.
 *
 * core-api refuses with 409 (code `409012`) while a live discount still targets the group; see
 * `isStillInUse`.
 * @param {string} merchantUrlname
 * @param {string} userGroupId
 * @returns {Promise<object>}
 */
export function deleteUserGroup(merchantUrlname, userGroupId) {
    return jsonApiRequest(`${dataUrl(merchantUrlname, 'usergroups')}/${userGroupId}`, { method: 'DELETE' });
}

/**
 * Whether a rejected core-api request failed only because the resource is still referenced
 * (409, error code `409012`, e.g. a user group some live discount targets).
 * @param {Error} error as thrown by `jsonApiRequest`
 * @returns {boolean}
 */
export function isStillInUse(error) {
    return error?.response?.status === 409
        && (error.body?.errors || []).some(e => e && e.code === '409012');
}
